import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { HoverCard } from "@mantine/core";
import type { FunctionComponent, PropsWithChildren } from "react";
import { useMemo } from "react";
import { generatePath, NavLink } from "react-router-dom";

import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner/LoadingSpinner";
import { TIME_TRACKINGS_PATH } from "~/constants/appRoutes.ts";
import type { StaffingClosingStatusesByMonth } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import { getAggregatedStaffingClosingStatuses } from "~/modules/billing/utils/billingUtils.ts";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = PropsWithChildren & {
	closingStatuses: StaffingClosingStatusesByMonth[];
	portalId: string;
	showBudget?: boolean;
	width?: number;
};

const formatMonthLabel = (dateString: string): string => {
	const date = new Date(dateString);
	const month = date.toLocaleString("de-DE", { month: "short" }).replace(".", "");
	if (date.getFullYear() !== new Date().getFullYear()) {
		return `${month} ${String(date.getFullYear()).slice(-2)}`;
	}
	return month;
};

const StaffingClosingStatusesHoverCard: FunctionComponent<Props> = ({
	children,
	closingStatuses,
	portalId,
	width = 280,
}) => {
	const {
		data: allUsersData,
		isLoading: allUsersIsLoading,
		isError: allUsersIsError,
	} = useAllUsers();

	const showMonthPills = closingStatuses.length > 1;

	const enrichedUsers = useMemo(() => {
		if (!allUsersData) return [];

		const aggregated = getAggregatedStaffingClosingStatuses(closingStatuses);

		return aggregated
			.map((staffedUser) => {
				const user = allUsersData.find((u) => u.id === staffedUser.userId);
				const unclosedMonths = closingStatuses.filter((s) =>
					s.staffingClosingStatuses.some(
						(sc) => sc.userId === staffedUser.userId && !sc.hasClosed,
					),
				);
				return {
					userId: staffedUser.userId,
					hasClosed: staffedUser.hasClosed,
					fullName: user?.fullName ?? "",
					unclosedMonths,
				};
			})
			.sort(byObjectProperty("fullName"));
	}, [allUsersData, closingStatuses]);

	const columnCount = Math.ceil(enrichedUsers.length / 10);

	return (
		<HoverCard
			width={columnCount > 1 ? width * columnCount : width}
			withinPortal={true}
			portalProps={{ target: "#" + portalId }}
			openDelay={0}
			closeDelay={20}
			shadow="md"
			position="right"
		>
			<HoverCard.Target>{children}</HoverCard.Target>
			<HoverCard.Dropdown className="rounded-md bg-primary-700 text-sm shadow-md">
				{allUsersIsLoading && (
					<div className="flex justify-center p-3">
						<LoadingSpinner theme="neutral" />
					</div>
				)}
				{allUsersIsError && (
					<p className="p-2 text-danger-500">Fehler beim Laden der Nutzer.</p>
				)}
				{!allUsersIsLoading && !allUsersIsError && (
					<ul
						className="p-1"
						style={{ columnCount, columnGap: "0.75rem" }}
					>
						{enrichedUsers.map((user) => {
							const monthNames = user.unclosedMonths.map((entry) => formatMonthLabel(entry.month)).join(", ");
							return (
								<li
									key={user.userId}

								>
									<NavLink to={generatePath(TIME_TRACKINGS_PATH, { userId: user.userId })}
											 className="group flex break-inside-avoid flex-wrap items-center gap-1 px-1 py-0.5 transition-colors">
										{user.hasClosed
											?
											<CheckCircleIcon className="size-4 shrink-0 text-success-500 group-hover:text-success-700" />
											:
											<XCircleIcon className="size-4 shrink-0 text-danger-500 group-hover:text-danger-700" />
										}
										<span className="relative shrink-0 text-xs">
										<span className="invisible font-bold">{user.fullName}</span>
										<span className="absolute inset-0 font-medium group-hover:font-bold">{user.fullName}</span>
									</span>
										{showMonthPills && monthNames &&
											<span
												className="relative text-xs text-danger-700 group-hover:font-bold"
											>
												<span className="invisible font-bold">{monthNames}</span>
												<span className="absolute inset-0 font-medium group-hover:font-bold">{monthNames}</span>
											</span>
										}
									</NavLink>
								</li>
							);
						})}
					</ul>
				)}
			</HoverCard.Dropdown>
		</HoverCard>
	);
};

export default StaffingClosingStatusesHoverCard;